import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import ImageInput from 'components/field/ImageInput';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Authorization from 'components/global/Authorization';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import ResetPasswordForm from 'pages/Users/ResetPasswordForm';
import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE, USERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const WHUserForm = NiceModal.create(({ id, modalName, mode }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					fullName: yup.string().nullable().required(),
					jobTitle: yup.string().nullable(),
					email: yup.string().email().nullable().required(),
					phone: yup.string().nullable().required().phoneNumber(),
					warehouseIDs: yup.array().nullable().required(),
					image: yup.string().nullable()
				})
				.required(),
		[]
	);
	const [isLoading, setIsLoading] = useState(false);

	// View mode for inputs
	const viewMode = mode === 'view';

	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();
	const handleCloseModal = () => closeModal();

	const defaultValues = {
		fullName: null,
		jobTitle: null,
		email: null,
		phone: null,
		warehouseIDs: null,
		avatarPath: null,
		otoPackUser: null,
		packerUser: null,
		pickerUser: null,
		putterUser: null
	};
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const fillForm = useCallback(async () => {
		try {
			const { data } = await axios.post(USERS.GET_USER, { ID: id });

			if (!data) {
				showError();
				closeModal();
				return;
			}
			const fillData = {};
			Object.keys(defaultValues).forEach((key) => {
				fillData[key] = data[key] ?? null;
			});

			reset(fillData);
		} catch (error) {
			AppLogger.error(error);
		}
	}, [id]);

	useEffect(() => {
		if (id) fillForm();
	}, [id]);

	const onSubmit = async (data) => {
		setIsLoading(true);
		const URL = id ? ADVANCE_WMS_MODULE.EDIT_WMS_USER : ADVANCE_WMS_MODULE.ADD_WMS_USER;
		id && (data.ID = id);
		try {
			const response = await axios.post(URL, data);
			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.WMS_USERS));
				closeModal();
				if (!id) {
					NiceModal.show(ResetPasswordForm, {
						resetLink: response.data.link,
						email: data.email
					});
				}
				return;
			}
			AppLogger.error(response);
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	const defaultColSpan = {
		md: 6
	};
	const switchColSpan = {
		md: 3,
		sm: 6
	};

	const warehouseAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	return (
		<Modal fullwidth name={modalName} maxWidth="small" className="modal-WHUserForm">
			<ModalTitle>
				<Typography variant="p1">
					{mode === 'view' ? 'viewUser' : mode === 'edit' ? 'editUser' : 'addUser'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} viewPrintButton={viewMode}>
					<FormItem colSpan={defaultColSpan} label="fullName" isLoading={isLoading} required>
						<StringInput disabled={viewMode} name="fullName" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="email" isLoading={isLoading} required>
						<StringInput disabled={viewMode || mode === 'edit'} name="email" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="jobTitle" isLoading={isLoading}>
						<StringInput disabled={viewMode} name="jobTitle" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isLoading} required>
						<PhoneNumber
							onChange={(value) => {
								setValue('phone', value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
							value={watch('phone')}
							name="phone"
						/>
					</FormItem>
					<FormItem colSpan={{ md: 12 }} label="userPhoto" isLoading={isLoading}>
						<ImageInput
							value={watch('avatarPath')}
							setValue={(value) => {
								setValue('avatarPath', value, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							placeholder="selectImage"
							name="avatarPath"
							disabled={viewMode}
							viewMode={viewMode}
						/>
					</FormItem>
					<FormItem
						label="assignedWarehouses"
						isLoading={isLoading}
						roleName="/autocomplete/warehouse"
						colSpan={{ md: 12 }}
						required>
						<AutoComplete
							disabled={viewMode}
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							value={watch('warehouseIDs')}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							name="warehouseIDs"
							multipleSelection
							setValue={(value) => {
								setValue('warehouseIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							firstItem={{
								action: () =>
									NiceModal.show(WarehouseFormShort, {
										setWarehouse: (value) => {
											setValue(`warehouseIDs`, [...(watch('warehouseIDs') ?? []), value], {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}),
								label: <Typography color="var(--blue)">addANewWarehouse</Typography>
							}}
						/>
					</FormItem>
					{/** WH User checks */}
					<FormItem colSpan={switchColSpan} label="otoPackUser" isLoading={isLoading}>
						<SwitchInput className="mt-3" disabled={viewMode} name="otoPackUser" formMode />
					</FormItem>
					<FormItem colSpan={switchColSpan} label="pickerUser" isLoading={isLoading}>
						<SwitchInput className="mt-3" disabled={viewMode || !watch('otoPackUser')} name="pickerUser" />
					</FormItem>
					<FormItem colSpan={switchColSpan} label="packerUser" isLoading={isLoading}>
						<SwitchInput className="mt-3" disabled={viewMode || !watch('otoPackUser')} name="packerUser" />
					</FormItem>
					<Authorization path="WMS_BinTracking">
						<FormItem colSpan={switchColSpan} label="putterUser" isLoading={isLoading}>
							<SwitchInput
								className="mt-3"
								disabled={viewMode || !watch('otoPackUser')}
								name="putterUser"
							/>
						</FormItem>
					</Authorization>
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : mode === 'view' ? 'button.view' : 'button.add'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default WHUserForm;
